import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { SpeechError, SpeechEvent, SpeechNotification } from '../components/mic/speech.models';

const { webkitSpeechRecognition } = window as any;

@Injectable({
  providedIn: 'root',
})
export class WebSpeechRecognizerService {
  recognition!: any;
  language!: string;
  isListening = false;

  constructor(private ngZone: NgZone) {}

  initialize(language: string): boolean {
    if ('webkitSpeechRecognition' in window) {
      try {
        this.recognition = new webkitSpeechRecognition();
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.setLanguage(language);
        return true;
      } catch (e) {
        console.log('error al crear el objeto recognition', e);
        return false;
      }
    }
    return false;
  }

  setLanguage(language: string): void {
    this.language = language;
    this.recognition.lang = language;
  }

  start() {
    if (!this.recognition) {
      return;
    }

    this.recognition.start();
    this.isListening = true;
  }

  onStart(): Observable<SpeechNotification> {
    if (!this.recognition) {
      this.initialize(this.language);
    }

    return new Observable((observer) => {
      this.recognition.onstart = () => {
        this.ngZone.run(() => {
          observer.next({
            event: SpeechEvent.Start,
          });
        });
      };
    });
  }

  onEnd(): Observable<SpeechNotification> {
    return new Observable((observer) => {
      this.recognition.onend = () => {
        this.ngZone.run(() => {
          observer.next({
            event: SpeechEvent.End,
          });
          this.isListening = false;
        });
      };
    });
  }

  onResult(): Observable<SpeechNotification> {
    return new Observable((observer) => {
      this.recognition.onresult = (event: any) => {
        let interimContent = '';
        let finalContent = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalContent += event.results[i][0].transcript;
            this.ngZone.run(() => {
              observer.next({
                event: SpeechEvent.FinalContent,
                content: finalContent,
              });
            });
          } else {
            interimContent += event.results[i][0].transcript;
            // console.log('interim transcript', event, interimContent);
            this.ngZone.run(() => {
              observer.next({
                event: SpeechEvent.InterimContent,
                content: interimContent,
              });
            });
          }
        }
      };
    });
  }

  onError(): Observable<SpeechNotification> {
    return new Observable((observer) => {
      this.recognition.onerror = (event) => {
        // tslint:disable-next-line:no-any
        const eventError: string = (event as any).error;
        let content: string = '';

        switch (eventError) {
          case SpeechError.NoSpeech:
            content = 'No se detecto ningun sonido';
            break;
          case SpeechError.NoSpeech:
            content = 'No puede capturar el audio';
            break;
          case SpeechError.NotAllowed:
            content = 'No tiene permiso para usar el microphono';
            break;
          case 'language-not-supported':
            const lang = event?.currentTarget?.lang;
            content = `Tu navegador no soporta el reconocimineto del idioma ${lang}`;
            break;
          default:
            content = eventError;
            break;
        }

        this.ngZone.run(() => {
          observer.next({
            event: event,
            error: eventError,
            content: content,
          });
        });
      };
    });
  }

  stop(): void {
    if (this.recognition) {
      this.recognition.stop();
    }
  }
}
